/**
 * batch-61 werkbon-gefactureerd (FO-449, spec 149) — het facturatie-spoor op de
 * werkbon, NAAST de operationele status (NIET_GEPLAND/GEPLAND/AFGEROND).
 *
 * Eén DTO (`WerkbonFacturatieDTO`) die lijsten, KPI's, detail én de klant-DTO
 * delen; de pure afleiding staat in `@streep/utils` (`facturatieStatusVan`), de
 * Prisma-fragmenten en mutaties in `apps/api/src/services/werkbon-facturatie.ts`.
 *
 * Bronnen van een GEFACTUREERD-markering:
 *   - FINQLE_VERZENDING  automatisch bij een (single/bulk/bundel) Finqle-verzending;
 *   - FINQLE_API         het INVOICED-event van het Finqle-facturatieverzoek (FO-406);
 *   - HANDMATIG          "Markeer als gefactureerd" met verplichte reden;
 *   - FACTUUR            een eigen STREEP-factuur is gekoppeld (wint altijd; een
 *                        handmatige markering wordt dan "vervangen door factuur X").
 */
import { z } from 'zod'
// batch-63 (FO-474) — rechtstreeks uit de blad-module (niet via facturen.ts):
// voorkomt een runtime-importcyclus werkbonnen -> werkbon-facturatie -> facturen.
import type { WerkbonNietFacturabelReden } from './werkbon-facturabel-reden'

export const WERKBON_FACTURATIE_BRONNEN = [
  'FINQLE_VERZENDING',
  'FINQLE_API',
  'HANDMATIG',
  'FACTUUR',
] as const
export type WerkbonFacturatieBron = (typeof WERKBON_FACTURATIE_BRONNEN)[number]

/** Korte bron-aanduiding achter "Gefactureerd". */
export const WERKBON_FACTURATIE_BRON_LABELS: Record<WerkbonFacturatieBron, string> = {
  FINQLE_VERZENDING: 'via Finqle',
  FINQLE_API: 'via Finqle',
  HANDMATIG: 'handmatig',
  FACTUUR: 'via factuur',
}

/**
 * De afgeleide status zoals de UI hem toont.
 *   NOG_TE_FACTUREREN  facturabel, (nog) geen markering en geen factuur;
 *   GEFACTUREERD       markering of factuurkoppeling aanwezig;
 *   NIET_FACTURABEL    interne (kantoor)bon of bewust niet-facturabel gezet (A7);
 *                      intern begrip, gaat NOOIT naar de klant-DTO.
 */
export const WERKBON_FACTURATIE_STATUSSEN = [
  'NOG_TE_FACTUREREN',
  'GEFACTUREERD',
  'NIET_FACTURABEL',
] as const
export type WerkbonFacturatieStatusKey = (typeof WERKBON_FACTURATIE_STATUSSEN)[number]

export const WERKBON_FACTURATIE_STATUS_LABELS: Record<WerkbonFacturatieStatusKey, string> = {
  NOG_TE_FACTUREREN: 'Nog te factureren',
  GEFACTUREERD: 'Gefactureerd',
  NIET_FACTURABEL: 'Niet te factureren',
}

export interface WerkbonFacturatieDTO {
  status: WerkbonFacturatieStatusKey
  /** Bron van de markering; null zolang niet gefactureerd. */
  bron: WerkbonFacturatieBron | null
  /** Zending-id (bundel) / mail-id (losse zending) / factuurnummer / vrije referentie. */
  referentie: string | null
  /** ISO-tijdstip van de markering (Finqle-verzending, INVOICED, handmatig, factuur). */
  op: string | null
  /** Reden bij een handmatige markering (of "vervangen door factuur X"). */
  reden: string | null
  /** De gekoppelde eigen factuur (niet de interne Finqle-proforma). */
  factuur: { id: string; invoiceNumber: string | null } | null
  /** Handmatig gemarkeerd en daarna door een eigen factuur vervangen (audit). */
  vervangenDoorFactuur: boolean
  /**
   * Terug te draaien ("Niet gefactureerd"): een handmatige markering zonder
   * factuurkoppeling, of (batch-63) een Finqle-verzending waar Finqle nog niets
   * mee deed (alleen lokaal bekend of afgewezen).
   */
  terugdraaibaar: boolean
  /**
   * batch-63 (FO-474) — alleen bij NIET_FACTURABEL: waarom. Een interne
   * (kantoor)bon heeft altijd INTERN (afgeleid uit `isIntern`). `null` = er is
   * geen reden vastgelegd; zie `zonderReden`.
   */
  nietFacturabelReden: WerkbonNietFacturabelReden | null
  /**
   * batch-63 (FO-474) — NIET_FACTURABEL zonder vastgelegde reden: een
   * omgevallen vlag die gecontroleerd moet worden (label "zonder reden -
   * controleren"; de dagelijkse controle telt precies deze bonnen).
   */
  zonderReden: boolean
  /**
   * Kant-en-klaar label: "Nog te factureren" / "Gefactureerd via Finqle" /
   * "Gefactureerd via factuur F2026-0012" / "Niet te factureren (garantie)".
   */
  label: string
}

// ─── Lijst-filter "Facturatiestatus" ─────────────────────────────────────────

export const WERKBON_FACTURATIE_FILTERS = [
  'nogTeFactureren',
  'gefactureerd',
  'finqle',
  'handmatig',
  'factuur',
  'nietFacturabel',
  // batch-63 (FO-474) — de werkvoorraad van de dagelijkse controle.
  'nietFacturabelZonderReden',
] as const
export type WerkbonFacturatieFilter = (typeof WERKBON_FACTURATIE_FILTERS)[number]

export const WERKBON_FACTURATIE_FILTER_LABELS: Record<WerkbonFacturatieFilter, string> = {
  nogTeFactureren: 'Nog te factureren',
  gefactureerd: 'Gefactureerd (alle)',
  finqle: 'Gefactureerd via Finqle',
  handmatig: 'Gefactureerd handmatig',
  factuur: 'Gefactureerd via factuur',
  nietFacturabel: 'Niet te factureren',
  nietFacturabelZonderReden: 'Niet te factureren zonder reden (controleren)',
}

export function isWerkbonFacturatieFilter(v: string | null | undefined): v is WerkbonFacturatieFilter {
  return (WERKBON_FACTURATIE_FILTERS as readonly string[]).includes(v ?? '')
}

// ─── Request-schema's (gedeeld door API en formulieren; inline veldfouten) ────

export const FACTURATIE_REDEN_MIN = 3
export const FACTURATIE_REDEN_MAX = 500
export const FACTURATIE_REFERENTIE_MAX = 100

const redenSchema = z
  .string({ error: 'Geef een reden op.' })
  .trim()
  .min(FACTURATIE_REDEN_MIN, `Geef een reden op (minimaal ${FACTURATIE_REDEN_MIN} tekens).`)
  .max(FACTURATIE_REDEN_MAX, `Maximaal ${FACTURATIE_REDEN_MAX} tekens.`)

const referentieSchema = z
  .string()
  .trim()
  .max(FACTURATIE_REFERENTIE_MAX, `Maximaal ${FACTURATIE_REFERENTIE_MAX} tekens.`)
  .nullable()
  .optional()

/** POST /werkbonnen/:id/facturatie/markeer */
export const MarkeerGefactureerdRequestSchema = z.object({
  reden: redenSchema,
  /** Optioneel factuurnummer/referentie van de externe facturatie. */
  referentie: referentieSchema,
})
export type MarkeerGefactureerdRequest = z.infer<typeof MarkeerGefactureerdRequestSchema>

/** POST /werkbonnen/:id/facturatie/terugdraaien */
export const TerugdraaienGefactureerdRequestSchema = z.object({
  reden: redenSchema,
})
export type TerugdraaienGefactureerdRequest = z.infer<typeof TerugdraaienGefactureerdRequestSchema>

export const FACTURATIE_BULK_MAX = 200

/** POST /werkbonnen/facturatie/bulk-markeer */
export const BulkMarkeerGefactureerdRequestSchema = z.object({
  werkbonIds: z
    .array(z.string().min(1))
    .min(1, 'Selecteer minimaal één werkbon.')
    .max(FACTURATIE_BULK_MAX, `Maximaal ${FACTURATIE_BULK_MAX} werkbonnen per keer.`),
  reden: redenSchema,
  referentie: referentieSchema,
})
export type BulkMarkeerGefactureerdRequest = z.infer<typeof BulkMarkeerGefactureerdRequestSchema>

// ─── Antwoorden ──────────────────────────────────────────────────────────────

/** Per-bon uitkomst van de bulk-markering (zelfde keys als de 409-codes van de single-route). */
export type MarkeerGefactureerdUitkomst =
  | 'OK'
  | 'NOT_FOUND'
  | 'NIET_GOEDGEKEURD'
  /** Interne (kantoor)bon of bewust niet-facturabel (A7): wordt nooit gefactureerd. */
  | 'NIET_FACTURABEL'
  | 'FACTUUR_GEKOPPELD'
  | 'AL_GEFACTUREERD'
  | 'ERROR'

export const MARKEER_GEFACTUREERD_UITKOMST_LABELS: Record<MarkeerGefactureerdUitkomst, string> = {
  OK: 'Gemarkeerd als gefactureerd',
  NOT_FOUND: 'Werkbon niet gevonden',
  NIET_GOEDGEKEURD: 'Nog niet intern goedgekeurd',
  NIET_FACTURABEL: 'Niet te factureren (intern of niet-facturabel)',
  FACTUUR_GEKOPPELD: 'Al aan een factuur gekoppeld',
  AL_GEFACTUREERD: 'Stond al op gefactureerd',
  ERROR: 'Onverwachte fout',
}

export interface BulkMarkeerGefactureerdItemDTO {
  id: string
  werkbonNumber: string | null
  status: MarkeerGefactureerdUitkomst
  reden?: string
}

export interface BulkMarkeerGefactureerdResultDTO {
  resultaten: BulkMarkeerGefactureerdItemDTO[]
  samenvatting: { gelukt: number; overgeslagen: number; mislukt: number }
}

/** Antwoord van de single markeer-/terugdraai-routes: de nieuwe stand. */
export interface WerkbonFacturatieMutatieDTO {
  werkbonId: string
  werkbonNumber: string
  facturatie: WerkbonFacturatieDTO
}

// ─── batch-63 werkbon-facturatie (FO-474) — controle + klantinstelling ────────

/**
 * GET /werkbonnen/facturatie/controle — de controlekaart op het
 * facturatie-overzicht. Server-side geteld over de VOLLEDIGE set (nooit over
 * geladen rijen); dezelfde telling als de dagelijkse controle.
 */
export interface WerkbonFacturatieControleDTO {
  /** Niet te factureren zonder vastgelegde reden (hoort structureel 0 te zijn). */
  nietFacturabelZonderReden: number
}

export const KLANT_INSTELLING_REDEN_MIN = 3
export const KLANT_INSTELLING_REDEN_MAX = 500

/**
 * PUT /clients/:id/werkbon-facturabel-default — de klant-default voor NIEUWE
 * werkbonnen. Uitzetten raakt elke volgende werkbon van deze klant (die krijgt
 * dan reden KLANT_INSTELLING), dus daar is een reden verplicht (audit).
 * Aanzetten mag zonder reden. Bestaande werkbonnen veranderen nooit mee.
 */
export const WerkbonFacturabelDefaultRequestSchema = z
  .object({
    werkbonFacturabelDefault: z.boolean(),
    reden: z
      .string()
      .trim()
      .max(KLANT_INSTELLING_REDEN_MAX, `Maximaal ${KLANT_INSTELLING_REDEN_MAX} tekens.`)
      .nullable()
      .optional(),
  })
  .superRefine((d, ctx) => {
    if (!d.werkbonFacturabelDefault && (d.reden ?? '').trim().length < KLANT_INSTELLING_REDEN_MIN) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reden'],
        message: `Geef een reden op (minimaal ${KLANT_INSTELLING_REDEN_MIN} tekens): nieuwe werkbonnen van deze klant gaan dan niet meer naar de facturatie.`,
      })
    }
  })
export type WerkbonFacturabelDefaultRequest = z.infer<typeof WerkbonFacturabelDefaultRequestSchema>

export interface WerkbonFacturabelDefaultWijzigingDTO {
  /** ISO-tijdstip van de wijziging. */
  op: string
  doorNaam: string | null
  van: boolean | null
  naar: boolean
  reden: string | null
}

/** GET/PUT /clients/:id/werkbon-facturabel-default */
export interface WerkbonFacturabelDefaultDTO {
  clientId: string
  werkbonFacturabelDefault: boolean
  /** De laatste geauditeerde wijziging; null = nooit via de app gewijzigd. */
  laatsteWijziging: WerkbonFacturabelDefaultWijzigingDTO | null
}
